import Vue from "vue";
import Vuex from "vuex";

import api from "@/api";

import {
  getLocalStorage,
  updateLocalStorage,
  clearLocalStorage,
} from "@/localStorage";

Vue.use(Vuex);

const LATEST_SCHEMA_VERSION = "1c38998a-add3-4a4f-8c76-686acf74a72c";

function get_default_state() {
  return {
    schemaVersion: LATEST_SCHEMA_VERSION,
    auth: {},
    user: {},
    invitations: {},
    teams: {},
    teamMembers: {},
    roles: {},
    teamInvitations: {},
    teamStatistics: {},
    teamShares: [],
    groups: {}, // schema: {groupId1:{...group1}, groupId2:{...group2}, etc}
    groupStatistics: {}, // map from group id to its statistics
    groupShares: {}, // map from group id to its shares
    activeTeamId: null, // todo: think of different way to manage this so multiple tabs of app can be open on different teams
    workers: {}, // map from worker id to general worker info
    workerCount: null, // get count of all workers in a team
    workerShares: {}, // map from worker id to its share data
    workerHashrate: {}, // map from worker id to its hash rate
    workerLastSubmission: {}, // map from worker id to its last submission
    deviceTypes: [],
    pools: [],
    toasts: [],
    alerts: [],
    license: {},
    billingInfo: {},
    billingCard: null,
    billingAddress: null,
    billingInvoices: {},
    invoices: [],
    default_payment_method: {},
    existing_payment_methods: [],
    teamContracts: [],
    permissions: {},
    accountRoles: {},
    autoRefreshEnabled: true,
  };
}

const store = new Vuex.Store({
  // throw errors if state is modified outside of mutation handlers while
  // in development (disable for production because this is expensive)
  strict: process.env.NODE_ENV !== "production",
  state: get_default_state(),
  mutations: {
    setAuth(state, newAuth) {
      state.auth = newAuth;
    },
    setUser(state, newUser) {
      state.user = newUser;
    },
    setInvitations(state, newInvitations) {
      state.invitations = newInvitations;
    },
    setActiveTeamId(state, activeTeamId) {
      state.activeTeamId = activeTeamId;

      // Clear other state upon switching active team
      state.invitations = {};
      state.teamInvitations = {};
      state.teamStatistics = {};
      state.teamShares = [];
      state.groups = {};
      state.groupStatistics = {};
      state.groupShares = {};
      state.workers = {};
      state.workerShares = {};
      state.workerHashrate = {};
      state.workerLastSubmission = {};
      state.license = {};
      state.teamMembers = {};
      state.roles = {};
    },
    setTeams(state, newTeams) {
      state.teams = newTeams;
    },
    setAutoRefreshEnabled(state, enabled) {
      state.autoRefreshEnabled = enabled;
    },
    setPermissions(state, permissions) {
      state.permissions = permissions;
    },
    setAccountRoles(state, roles) {
      state.accountRoles = roles;
    },
    setRoles(state, newRoles) {
      state.roles = newRoles;
    },
    setRole(state, role) {
      Vue.set(state.roles, role.id, role);
    },
    deleteRole(state, roleId) {
      Vue.delete(state.roles, roleId);
    },
    setTeam(state, newTeam) {
      Vue.set(state.teams, newTeam.id, newTeam);
    },
    setTeamMembers(state, newMembers) {
      state.teamMembers = newMembers;
    },
    removeTeamMember(state, memberId) {
      Vue.delete(state.teamMembers, memberId);
    },
    setTeamMember(state, member) {
      Vue.set(state.teamMembers, member.id, member);
    },
    setBillingInfo(state, billingInfo) {
      state.billingInfo = billingInfo;
    },
    setBillingCard(state, billingCard) {
      state.billingCard = billingCard;
    },
    setBillingAddress(state, billingAddress) {
      state.billingAddress = billingAddress;
    },
    setBillingInvoice(state, invoice) {
      Vue.set(state.billingInvoices, invoice.id, invoice);
    },
    setBillingInvoices(state, invoices) {
      state.billingInvoices = invoices;
    },
    setLicense(state, license) {
      state.license = license;
    },
    setTeamStatistics(state, newTeamStatistics) {
      state.teamStatistics = newTeamStatistics;
    },
    setTeamShares(state, data) {
      state.teamShares = data;
    },
    setTeamInvitations(state, newTeamInvitations) {
      state.teamInvitations = newTeamInvitations;
    },
    setTeamInvitation(state, newTeamInvitation) {
      Vue.set(state.teamInvitations, newTeamInvitation.id, newTeamInvitation);
    },
    setGroups(state, newGroups) {
      state.groups = newGroups;
    },
    setGroup(state, newGroup) {
      Vue.set(state.groups, newGroup.id, newGroup);
    },
    setGroupStatisticsMulti(state, { groupIds, newGroupStatistics }) {
      if (groupIds.length == 1) {
        newGroupStatistics = [newGroupStatistics];
      }
      for (var i = 0; i < groupIds.length; i++) {
        Vue.set(state.groupStatistics, groupIds[i], newGroupStatistics[i]);
      }
    },
    setGroupStatistics(state, { group, newGroupStatistics }) {
      // The below line maintains reactivity while doing the equivalent of:
      // state.groupStatistics[group] = newGroupStatistics;
      Vue.set(state.groupStatistics, group, newGroupStatistics);
    },
    setGroupShares(state, { group, data }) {
      Vue.set(state.groupShares, group, data);
    },
    setGroupSharesMulti(state, { groupIds, data }) {
      for (var i = 0; i < groupIds.length; i++) {
        Vue.set(state.groupShares, groupIds[i], data[i]);
      }
    },
    pushToast(state, newToast) {
      state.toasts.push(newToast);
    },
    shiftToasts(state) {
      state.toasts.shift();
    },
    clearWorkers(state) {
      state.workers = {};
    },
    setWorkers(state, newWorkers) {
      state.workers = newWorkers;
    },
    setWorker(state, newWorker) {
      Vue.set(state.workers, newWorker.id, newWorker);
    },
    deleteWorker(state, workerId) {
      Vue.delete(state.workers, workerId);
    },
    deleteGroup(state, groupId) {
      Vue.delete(state.groups, groupId);
    },
    setWorkerCount(state, count) {
      state.workerCount = count;
    },
    setWorkerShares(state, { worker, newWorkerShares }) {
      Vue.set(state.workerShares, worker, newWorkerShares);
    },
    setWorkerHashrate(state, { worker, hashrate }) {
      Vue.set(state.workerHashrate, worker, hashrate);
    },
    setWorkerLastSubmission(state, { worker, lastSubmission }) {
      Vue.set(state.workerLastSubmission, worker, lastSubmission);
    },
    setDeviceTypes(state, newDeviceTypes) {
      state.deviceTypes = newDeviceTypes;
    },
    setPools(state, newPools) {
      state.pools = newPools;
    },
    setAlerts(state, newAlerts) {
      state.alerts = newAlerts;
    },
    setAlert(state, newAlert) {
      Vue.set(state.alerts, newAlert.id, newAlert);
    },
    deleteAlert(state, alertId) {
      Vue.delete(state.alerts, alertId);
    },
    deleteTeam(state, teamId) {
      Vue.delete(state.teams, teamId);
    },
    updateTeamData(state, newName) {
      let currentTeam = this.getters.activeTeam;
      currentTeam.name = newName;
      Vue.set(state.teams, currentTeam.id, currentTeam);
    },
    setInvoices(state, invoices) {
      state.invoices = invoices;
    },
    setDefaultPaymentMethod(state, default_payment_method) {
      state.default_payment_method = default_payment_method;
    },
    setTeamContracts(state, contracts) {
      state.teamContracts = contracts;
    },
    setPaymentMethodArray(state, payment_methods) {
      state.existing_payment_methods = payment_methods;
    },
    initializeStore(state) {
      // Get any state stored in local storage
      const newState = getLocalStorage();

      // Check schema version of state stored in local storage
      if (newState.schemaVersion === LATEST_SCHEMA_VERSION) {
        // If schemas match, then replace the state with what's in local storage
        // (merged into the default initialized state)
        this.replaceState(Object.assign(state, newState));
      } else {
        // If schemas don't match, then clear local storage
        // and proceed with default initialized state
        clearLocalStorage();
      }
    },
    resetStore(state) {
      // Replace the state on this vuex instance with the default state
      Object.assign(state, get_default_state());
    },
  },
  getters: {
    can(state, getters) {
      let can = {};
      Object.values(state.permissions).forEach((permission) => {
        can[permission.entity] = can[permission.entity] || {};
        can[permission.entity][permission.action] = false;
      });
      getters.activeRole.permissions.forEach((permission) => {
        can[permission.entity][permission.action] = true;
      });
      return can;
    },
    activeRole(state) {
      return Object.values(state.accountRoles).find((role) => {
        return role.team_id == state.activeTeamId;
      });
    },
    accessToken(state) {
      return state.auth.access_token;
    },
    authenticated(state) {
      return !!state.auth.access_token;
    },
    activeTeam(state) {
      const activeTeam = Object.values(state.teams).find((el) => {
        return el.id === state.activeTeamId;
      });

      if (activeTeam) {
        return activeTeam;
      } else {
        return {};
      }
    },
  },
  actions: {
    async signUp(
      context,
      { email, password, handle, name, marketingEmailsEnabled, invitationId }
    ) {
      let fetched = await api.signUp(
        email,
        password,
        handle,
        name,
        marketingEmailsEnabled,
        invitationId
      );

      if (!fetched.ok) {
        switch (fetched.data.error.code) {
          case "DUPLICATE_ACCOUNT_HANDLE":
            context.commit("pushToast", {
              title: "Error",
              message: "This handle is already taken.",
              variant: "danger",
            });
            break;
          case "MISSING_ACCOUNT_NAME":
            context.commit("pushToast", {
              title: "Error",
              message: "You must submit an account name",
              variant: "danger",
            });
            break;
          case "TOO_LONG_ACCOUNT_EMAIL":
            context.commit("pushToast", {
              title: "Error",
              message: "Email Address is too long. Try another.",
              variant: "danger",
            });
            break;
          default:
            context.commit("pushToast", {
              title: "Error",
              message: "Sign Up Failed",
              variant: "danger",
            });
        }

        return fetched;
      }

      context.commit("pushToast", {
        title: "Success",
        message: "Sign up successful",
        variant: "success",
      });

      context.commit("setAuth", fetched.data.result);

      return fetched;
    },
    async getInvitation(context, { invitationId }) {
      let fetched = await api.getInvitation(invitationId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get invitation details",
          variant: "danger",
        });

        return false;
      }

      return fetched.data.result.invitation;
    },
    async verify(context, { email, token }) {
      let fetched = await api.verify(email, token);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not verify email",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: "Successfully verified email",
        variant: "success",
      });

      context.commit("setAuth", fetched.data.result);

      return true;
    },
    async signIn(context, { email, password, twoFactorCode }) {
      let response = await api.signIn(email, password, twoFactorCode);

      if (response.ok) {
        context.commit("setAuth", response.data.result);
      }

      return response.data;
    },
    async resetPassword(context, { email }) {
      let response = await api.resetPassword(email);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Sign in unsuccessful",
          variant: "danger",
        });

        return false;
      }

      return true;
    },
    async setPassword(context, { email, password, token }) {
      let response = await api.setPassword(email, password, token);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to set password",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully reset password`,
        variant: "success",
      });

      return true;
    },
    async changePassword(context, { password, newPassword }) {
      let response = await api.changePassword(password, newPassword);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to change password",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Password changed successfully`,
        variant: "success",
      });

      return true;
    },
    async enableTwoFactor(context) {
      let response = await api.enableTwoFactor();

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to enable two-factor authentication.",
          variant: "danger",
        });

        return null;
      }

      return response.data.result;
    },
    async disableTwoFactor(context) {
      let response = await api.disableTwoFactor();

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to disable two-factor authentication.",
          variant: "danger",
        });

        return false;
      }

      return true;
    },
    async confirmTwoFactor(context, { code }) {
      let response = await api.confirmTwoFactor(code);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to enable two-factor authentication.",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully enabled two-factor authentication`,
        variant: "success",
      });

      return true;
    },
    async resendEmailConfirmation(context, { email }) {
      let response = await api.resendEmailConfirmation(email);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to resend confirmation email",
          variant: "danger",
        });

        return false;
      }

      return true;
    },
    async addSupportMessage(context, { subject, body, name, email }) {
      let response = await api.addSupportMessage(subject, body, name, email);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Unable to submit support message",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully submitted support message`,
        variant: "success",
      });

      return true;
    },
    async getAlertEvents(
      context,
      { groupId, alertId, limit, offset, format, timezone }
    ) {
      const teamId = groupId || alertId ? null : this.state.activeTeamId;

      let fetched = await api.getAlertEvents(
        teamId,
        groupId,
        alertId,
        limit,
        offset,
        format,
        timezone
      );

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get alert events",
          variant: "danger",
        });

        return false;
      }

      return fetched.data.result;
    },
    async refreshLogIn(context) {
      let refresh_token = this.state.auth.refresh_token;

      let refreshFetch = await api.refreshLogIn(refresh_token);

      if (!refreshFetch.ok) {
        return false;
      }

      context.commit("setAuth", refreshFetch.data.result);

      return true;
    },
    async getUser(context) {
      let getUserFetch = await api.getUser();

      if (!getUserFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch user",
          variant: "danger",
        });

        return false;
      }

      context.commit("setUser", getUserFetch.data.result.account);

      return true;
    },
    async putAccount(context, { name, handle, marketingEmailsEnabled }) {
      let response = await api.putAccount(name, handle, marketingEmailsEnabled);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update account settings",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully updated account settings`,
        variant: "success",
      });

      context.commit("setUser", response.data.result.account);

      return true;
    },
    async getInvitations(context, fields) {
      let fetched = await api.getInvitations(fields);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch user invitations",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setInvitations",
        _arrayToMap(fetched.data.result.invitations)
      );

      return true;
    },
    async acceptInvitation(context, invitationId) {
      let fetched = await api.acceptInvitation(invitationId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not accept user invitation",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully accepted invitation`,
        variant: "success",
      });

      // Reload invitations
      await context.dispatch("getInvitations", [
        "id,rejected,accepted,created,created_by.handle,role.name",
      ]);

      return true;
    },
    async rejectInvitation(context, invitationId) {
      let fetched = await api.rejectInvitation(invitationId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not reject user invitation",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: "You have rejected the invitation",
        variant: "warning",
      });

      // Reload invitations
      await context.dispatch("getInvitations", [
        "id,rejected,accepted,created,created_by.handle,role.name",
      ]);

      return true;
    },
    async getTeams(context) {
      let getTeamsFetch = await api.getTeams();

      if (!getTeamsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch teams",
          variant: "danger",
        });

        return false;
      }
      context.commit("setTeams", _arrayToMap(getTeamsFetch.data.result.teams));

      return true;
    },
    async getAllTeamsForContracts(context) {
      let getAllTeamsFetch = await api.getAllTeamsForContracts();

      if (!getAllTeamsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch all the teams",
          variant: "danger",
        });

        return false;
      }

      return getAllTeamsFetch.data.result.teams;
    },
    async getTeamMembers(context, fields) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getTeamMembers(teamId, fields);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch team members",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setTeamMembers",
        _arrayToMap(fetched.data.result.members)
      );

      return fetched.data.result;
    },
    //HARCODED FOR TESTING/SETUP OF UI
    async getMetadata(context) {
      const metadata = {
        facility: "CCMA",
        securityDeposit: 100,
        insurance: 23,
        compatibility: true,
        wattage: 3.25,
        newTeam: true,
        termLengthInYears: "3",
        setUpFee: 12,
        quantity: 11,
        model: "S19 100T",
        pricePerKWH: 0.059,
        shippedOrTransferred: "transferred",
        customerType: "Blockware",
        batch: "002",
      };

      context.commit("setMetadata", metadata);

      return metadata;
    },

    async createOneTimeCharge(context, { contract }) {
      let fetched = await api.createOnboardingCharge(contract);
      return {
        ok: fetched.ok,
        data: fetched.data,
      };
    },

    async saveCreditCard(context, { paymentMethodId }) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.saveCreditCard({ paymentMethodId, teamId });
      return {
        ok: fetched.ok,
        data: fetched.data,
      };
    },

    async createLinkToken() {
      let teamId = this.state.activeTeamId;
      let fetched = await api.createLinkToken(teamId);
      return {
        ok: fetched.ok,
        data: fetched.data,
      };
    },

    async saveBankAccount(context, { public_token, metadata }) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.saveBankAccount({
        public_token,
        metadata,
        teamId,
      });
      return {
        ok: fetched.ok,
        data: fetched.data,
      };
    },

    async getBillingInvoices(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getBillingInvoices(teamId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch invoices",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setBillingInvoices",
        _arrayToMap(fetched.data.result.invoices)
      );

      return true;
    },
    async getBillingInvoice(context, { invoiceId }) {
      let fetched = await api.getBillingInvoice(invoiceId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get invoice ${invoiceId}`,
          variant: "danger",
        });

        return false;
      }

      // Save to state
      context.commit("setBillingInvoice", fetched.data.result.invoice);

      // Also return worker to caller
      return fetched.data.result.invoice;
    },
    async getBillingInfo(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getBillingInfo(teamId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not retrieve billing info",
          variant: "danger",
        });

        return false;
      }

      context.commit("setBillingInfo", fetched.data.result.summary);

      return true;
    },
    async putBillingPayment(
      context,
      {
        firstName,
        lastName,
        line1,
        line2,
        city,
        state,
        zipCode,
        country,
        token,
      }
    ) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.putBillingPayment(
        teamId,
        firstName,
        lastName,
        line1,
        line2,
        city,
        state,
        zipCode,
        country,
        token
      );

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not save payment information",
          variant: "danger",
        });

        return false;
      }

      context.commit("setBillingAddress", fetched.data.result.address);
      context.commit("setBillingCard", fetched.data.result.card);

      return true;
    },
    async getBillingPayment(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getBillingPayment(teamId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not retrieve payment information",
          variant: "danger",
        });

        return false;
      }

      context.commit("setBillingAddress", fetched.data.result.address);
      context.commit("setBillingCard", fetched.data.result.card);

      return true;
    },
    async getLicense(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getLicense(teamId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch license info",
          variant: "danger",
        });

        return false;
      }

      context.commit("setLicense", fetched.data.result.license);

      return fetched.data.result;
    },
    async upgradeLicense(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.upgradeLicense(teamId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not upgrade",
          variant: "danger",
        });

        return false;
      }

      context.commit("setLicense", fetched.data.result.license);

      return fetched.data.result;
    },
    async downgradeLicense(context, { keepWorkerIds }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.downgradeLicense(teamId, keepWorkerIds);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not downgrade",
          variant: "danger",
        });

        return false;
      }

      context.commit("setLicense", fetched.data.result.license);

      return fetched.data.result;
    },
    async getAccountRoles(context) {
      let fetched = await api.getAccountRoles();

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch account roles",
          variant: "danger",
        });

        return false;
      }

      context.commit("setAccountRoles", _arrayToMap(fetched.data.result.roles));

      return true;
    },
    async getRoles(context, fields) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getRoles(teamId, fields);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch team roles",
          variant: "danger",
        });

        return false;
      }

      context.commit("setRoles", _arrayToMap(fetched.data.result.roles));

      return true;
    },
    async getRole(context, { roleId }) {
      let fetched = await api.getRole(roleId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch role",
          variant: "danger",
        });

        return false;
      }

      context.commit("setRole", fetched.data.result.role);

      return true;
    },
    async getPermissions(context) {
      let fetched = await api.getPermissions();

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not fetch permissions",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setPermissions",
        _arrayToMap(fetched.data.result.permissions)
      );

      return true;
    },
    // todo def
    async getTeamStatistics(context, { team_obj = null, save = true }) {
      // todo: don't hardcode; turn into parameters
      const teamId = team_obj !== null ? team_obj.id : this.state.activeTeamId;

      // Team statistics is just group statistics for the group "default"
      let getGroupStatisticsFetch = await api.getGroupStatistics(
        teamId,
        "default"
      );

      if (!getGroupStatisticsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get team statistics",
          variant: "danger",
        });

        return false;
      }

      if (save !== false) {
        context.commit(
          "setTeamStatistics",
          getGroupStatisticsFetch.data.result
        );
        return true;
      }

      return getGroupStatisticsFetch.data.result;
    },
    // todo def
    async getTeamShares(context, { team_obj = null, save = true }) {
      // todo: don't hardcode; turn into parameters
      const teamId = team_obj !== null ? team_obj.id : this.state.activeTeamId;

      let fetched = await api.getGroupShares(teamId, "default");

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get team shares`,
          variant: "danger",
        });

        return false;
      }

      // Normalize share data
      const shareData = fetched.data.result.share_data;
      let normalized = [];

      let timezone = -new Date().getTimezoneOffset();
      let interval_start = new Date(
        new Date(shareData.interval_start).valueOf() + timezone * 60000
      );
      let interval_increment = 60000 * shareData.interval_increments;
      for (let i = 0; i < shareData.values.length; i++) {
        normalized.push([
          interval_start.getTime() + interval_increment * i,
          shareData.values[i],
        ]);
      }

      if (save) {
        context.commit("setTeamShares", normalized);
        return true;
      }

      return normalized;
    },
    // todo def
    async getTeamInvitations(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getTeamInvitations(teamId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get team invitations",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setTeamInvitations",
        _arrayToMap(fetched.data.result.invitations)
      );

      return true;
    },
    async postTeam(context, { name, handle }) {
      let postTeamsFetch = await api.postTeam(name, handle);

      if (!postTeamsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create team",
          variant: "danger",
        });

        return null;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created team: ${name}`,
        variant: "success",
      });

      context.commit("setTeam", postTeamsFetch.data.result.team);

      return postTeamsFetch.data.result.team;
    },
    async postTeamInvitations(context, { roleId, email }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.postTeamInvitations(teamId, roleId, email);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not send team invitation",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully sent team invitation to ${email}`,
        variant: "success",
      });

      // Todo: uncomment when this endpoint actually returns list of newly created team invitations
      // context.commit("setTeamInvitations", _arrayToMap(fetched.data.result.invitations));

      return true;
    },
    async getGroups(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getGroupsFetch = await api.getGroups(teamId);

      if (!getGroupsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get groups",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setGroups",
        _arrayToMap(getGroupsFetch.data.result.groups)
      );

      return true;
    },
    async getFacilities(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getFacilitiesFetch = await api.getFacilities(teamId);

      if (!getFacilitiesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get facilities",
          variant: "danger",
        });

        return false;
      }

      context.commit(
        "setFacilities",
        _arrayToMap(getFacilitiesFetch.data.result.facilities)
      );

      return true;
    },
    async getContractFacilities(context) {
      let getFacilitiesFetch = await api.getContractFacilities();

      if (!getFacilitiesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get contract facilities",
          variant: "danger",
        });

        return false;
      }

      return getFacilitiesFetch.data.result.contract_facilities;
    },
    async getContractFacility(context, { contractFacilityId }) {
      let getFacilitiesFetch = await api.getContractFacility(
        contractFacilityId
      );

      if (!getFacilitiesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get contract facility with ID: ${contractFacilityId}`,
          variant: "danger",
        });

        return false;
      }

      return getFacilitiesFetch.data.result.contract_facility;
    },
    async deleteContractFacility(context, { contractFacilityId }) {
      let fetch = await api.deleteContractFacility(contractFacilityId);

      if (!fetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get contract facility with ID: ${contractFacilityId}`,
          variant: "danger",
        });

        return false;
      }

      return true;
    },
    async putContractFacility(
      context,
      { contractFacilityId, name, contact_email }
    ) {
      let getFacilitiesFetch = await api.putContractFacility(
        contractFacilityId,
        name,
        contact_email
      );

      if (!getFacilitiesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not update contract facility`,
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: "Successfully updated contract facility",
        variant: "success",
      });

      return true;
    },
    async postContractFacility(context, { name, contact_email, handle }) {
      let response = await api.postContractFacility(
        name,
        contact_email,
        handle
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could create contract facility",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created contract facility: ${name}`,
        variant: "success",
      });

      return true;
    },
    async getAlert(context, { alertId }) {
      let getAlertFetch = await api.getAlert(alertId);

      if (!getAlertFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get alert with ID: ${alertId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setAlert", getAlertFetch.data.result.alert);

      return true;
    },
    async deleteTeam(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let response = await api.deleteTeam(teamId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not delete team`,
          variant: "danger",
        });

        return false;
      }

      context.commit("deleteTeam", teamId);

      let firstTeam = Object.keys(this.state.teams)[0];
      context.commit("setActiveTeamId", firstTeam);

      return true;
    },
    async leaveTeam(context) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let response = await api.leaveTeam(teamId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not leave team`,
          variant: "danger",
        });

        return false;
      }

      context.commit("deleteTeam", teamId);

      let firstTeam = Object.keys(this.state.teams)[0];
      context.commit("setActiveTeamId", firstTeam);

      return true;
    },
    async updateTeam(context, { teamId, newName, handle }) {
      let response = await api.putTeam(teamId, newName, handle);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update team name",
          variant: "danger",
        });

        return false;
      }

      context.commit("updateTeamData", newName);
      context.commit("pushToast", {
        title: "Success",
        message: "Successfully updated team name",
        variant: "success",
      });

      return true;
    },
    async deleteAlert(context, { alertId }) {
      let response = await api.deleteAlert(alertId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not delete alert with ID: ${alertId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("deleteAlert", alertId);

      return true;
    },
    async deleteRole(context, { roleId }) {
      let response = await api.deleteRole(roleId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not delete role`,
          variant: "danger",
        });

        return false;
      }

      context.commit("deleteRole", roleId);

      return true;
    },
    async removeTeamMember(context, { memberId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let response = await api.removeTeamMember(teamId, memberId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not remove team member`,
          variant: "danger",
        });

        return false;
      }

      context.commit("removeTeamMember", memberId);

      return true;
    },
    async putTeamMember(context, { memberId, roleId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let response = await api.putTeamMember(teamId, memberId, roleId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not update team member`,
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully updated team member`,
        variant: "success",
      });

      context.commit("setTeamMember", response.data.result.member);

      return true;
    },
    async getAlerts(context, { team_obj = null, save = true }) {
      // todo: don't hardcode; turn into parameters
      const teamId = team_obj !== null ? team_obj.id : this.state.activeTeamId;

      let getAlertsFetch = await api.getAlerts(teamId);

      if (!getAlertsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get alerts",
          variant: "danger",
        });

        return false;
      }

      if (save !== false) {
        context.commit(
          "setAlerts",
          _arrayToMap(getAlertsFetch.data.result.alerts)
        );
      }

      return getAlertsFetch.data.result;
    },
    async enableAlert(context, { alertId }) {
      let response = await api.enableAlert(alertId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not enable alert",
          variant: "danger",
        });

        return false;
      }

      context.commit("setAlert", response.data.result.alert);

      return true;
    },
    async disableAlert(context, { alertId }) {
      let response = await api.disableAlert(alertId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not disable alert",
          variant: "danger",
        });

        return false;
      }

      context.commit("setAlert", response.data.result.alert);

      return true;
    },
    async postAlert(
      context,
      {
        name,
        kind,
        handle,
        groupId,
        parameters,
        accountIds,
        notes,
        enabled,
        notifyOnResolve,
        notifyRepeatDelay,
        timeThreshold,
        notifyOnChange,
        hashrateThreshold,
      }
    ) {
      let response = await api.postAlert(
        name,
        kind,
        handle,
        groupId,
        parameters,
        accountIds,
        notes,
        enabled,
        notifyOnResolve,
        notifyRepeatDelay,
        timeThreshold,
        notifyOnChange,
        hashrateThreshold
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create alert",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created alert: ${name}`,
        variant: "success",
      });

      context.commit("setAlert", response.data.result.alert);

      return true;
    },
    async putAlert(
      context,
      {
        alertId,
        name,
        kind,
        handle,
        groupId,
        parameters,
        accountIds,
        notes,
        enabled,
        notifyOnResolve,
        notifyRepeatDelay,
        timeThreshold,
        notifyOnChange,
        hashrateThreshold,
      }
    ) {
      let response = await api.putAlert(
        alertId,
        name,
        kind,
        handle,
        groupId,
        parameters,
        accountIds,
        notes,
        enabled,
        notifyOnResolve,
        notifyRepeatDelay,
        timeThreshold,
        notifyOnChange,
        hashrateThreshold
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update alert",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully updated alert: ${name}`,
        variant: "success",
      });

      context.commit("setAlert", response.data.result.alert);

      return true;
    },

    async postRole(
      context,
      {
        name,
        handle,
        description,
        limitedGroupAccess,
        permissionIds,
        accessibleGroupIds,
      }
    ) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let response = await api.postRole(
        teamId,
        name,
        handle,
        description,
        limitedGroupAccess,
        permissionIds,
        accessibleGroupIds
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create role",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created role: ${name}`,
        variant: "success",
      });

      context.commit("setRole", response.data.result.role);

      return response.data.result.role;
    },
    async putRole(
      context,
      {
        roleId,
        name,
        handle,
        description,
        limitedGroupAccess,
        permissionIds,
        accessibleGroupIds,
      }
    ) {
      let response = await api.putRole(
        roleId,
        name,
        handle,
        description,
        limitedGroupAccess,
        permissionIds,
        accessibleGroupIds
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update role",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully updated role: ${name}`,
        variant: "success",
      });

      context.commit("setRole", response.data.result.role);

      return true;
    },
    async postGroup(context, { name, handle, notes, workerIds }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let postGroupFetch = await api.postGroup(
        teamId,
        name,
        handle,
        notes,
        workerIds
      );

      if (!postGroupFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create group",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created group: ${name}`,
        variant: "success",
      });

      context.commit("setGroup", postGroupFetch.data.result.group);

      return true;
    },
    async getGroup(context, { groupId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getGroupFetch = await api.getGroup(teamId, groupId);

      if (!getGroupFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get group with ID: ${groupId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setGroup", getGroupFetch.data.result.group);

      return true;
    },
    async getFacility(context, { facilityId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getFacilityFetch = await api.getFacility(teamId, facilityId);

      if (!getFacilityFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get facility with ID: ${facilityId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setFacility", getFacilityFetch.data.result.facility);

      return true;
    },
    async getFacilityModules(context, { facilityId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getFacilityModulesFetch = await api.getFacilityModules(
        teamId,
        facilityId
      );

      if (!getFacilityModulesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get modules for facility with ID: ${facilityId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setFacilityModules", {
        newFacilityId: facilityId,
        newFacilityModules: getFacilityModulesFetch.data.result.modules,
      });

      return true;
    },
    async updateFacility(
      context,
      {
        facilityId,
        module_type,
        module_id,
        min_wattage,
        max_wattage,
        target_wattage,
        display_order,
        notes,
      }
    ) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let updateFacilityFetch = await api.updateFacility(
        teamId,
        module_type,
        module_id,
        min_wattage,
        max_wattage,
        target_wattage,
        display_order,
        notes
      );

      if (!updateFacilityFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update facility target",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully updated facility target.`,
        variant: "success",
      });

      var newFacilityModulesArray = [];
      newFacilityModulesArray.push(updateFacilityFetch.data.result.module);
      context.commit("setFacilityModules", {
        newFacilityId: facilityId,
        newFacilityModules: newFacilityModulesArray,
      });

      return true;
    },
    async getGroupStatisticsMulti(context, { groupIds }) {
      let getGroupStatisticsFetch = await api.getGroupStatisticsMulti(groupIds);

      if (!getGroupStatisticsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get group statistics for ${groupIds.length} groups`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setGroupStatisticsMulti", {
        groupIds: groupIds,
        newGroupStatistics: getGroupStatisticsFetch.data.result,
      });

      return true;
    },
    async getGroupStatistics(context, { groupId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getGroupStatisticsFetch = await api.getGroupStatistics(
        teamId,
        groupId
      );

      if (!getGroupStatisticsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get group statistics for group: ${groupId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setGroupStatistics", {
        group: groupId,
        newGroupStatistics: getGroupStatisticsFetch.data.result,
      });

      return true;
    },
    async getGroupSharesMulti(context, { groupIds }) {
      let fetched = await api.getGroupSharesMulti(groupIds);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get group shares for ${groupIds.length} groups`,
          variant: "danger",
        });

        return false;
      }

      var result = [];

      var fetchedData = fetched.data.result;
      if (groupIds.length == 1) {
        fetchedData = [fetched.data.result];
      }

      for (let i = 0; i < fetchedData.length; i++) {
        // Normalize worker share data
        let shareData = fetchedData[i].share_data;
        let normalized = [];
        let timezone = -new Date().getTimezoneOffset();
        let interval_start = new Date(
          new Date(shareData.interval_start).valueOf() + timezone * 60000
        );
        let interval_increment = 60000 * shareData.interval_increments;
        for (let j = 0; j < shareData.values.length; j++) {
          normalized.push([
            interval_start.getTime() + interval_increment * j,
            shareData.values[j],
          ]);
        }
        result.push(normalized);
      }

      context.commit("setGroupSharesMulti", {
        groupIds,
        data: result,
      });

      return true;
    },
    async getGroupShares(context, { groupId, resolution }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.getGroupShares(teamId, groupId, resolution);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get group shares for group: ${groupId}`,
          variant: "danger",
        });

        return false;
      }

      // Normalize worker share data
      const shareData = fetched.data.result.share_data;

      let normalized = [];
      let timezone = -new Date().getTimezoneOffset();
      let interval_start = new Date(
        new Date(shareData.interval_start).valueOf() + timezone * 60000
      );
      let interval_increment = 60000 * shareData.interval_increments;
      for (let i = 0; i < shareData.values.length; i++) {
        normalized.push([
          interval_start.getTime() + interval_increment * i,
          shareData.values[i],
        ]);
      }

      context.commit("setGroupShares", {
        group: groupId,
        data: normalized,
      });

      return true;
    },

    async putGroup(context, { groupId, name, handle, notes, workerIds }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.putGroup(
        teamId,
        groupId,
        name,
        handle,
        notes,
        workerIds
      );

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not update group: ${groupId}`,
          variant: "danger",
        });

        return false;
      }

      context.commit("setGroup", fetched.data.result.group);

      return true;
    },
    async getAllWorkers(context) {
      // This function doesn't parallelize requests for workers;
      // For now, I think this is fine, because it probably
      // won't be a huge latency problem, and it lessens the
      // load on our API server

      // Number of workers to retrieve per query
      const batchSize = 50;

      // get total number of workers
      await context.dispatch("getTeamStatistics");
      const totalWorkers = context.state.teamStatistics.count;

      // Clear workers
      context.commit("clearWorkers");

      // todo: disable notifications for getWorkers to prevent flooding if network down
      // Fetch and add workers
      for (let offset = 0; offset < totalWorkers; offset += batchSize) {
        const success = await context.dispatch("getWorkers", {
          offset,
          batchSize,
        });
        if (!success) {
          return false;
        }
      }

      return true;
    },
    async getWorkerCount(context) {
      const teamId = this.state.activeTeamId;
      let response = await api.getWorkerCount(teamId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get worker count",
          variant: "danger",
        });

        return false;
      }

      context.commit("setWorkerCount", response.data.result.count);

      return response.data.result.count;
    },
    // todo def
    async getWorkers(context, { limit, offset, searchString, fields }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let response = await api.getWorkers(
        teamId,
        limit,
        offset,
        searchString,
        fields
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get workers",
          variant: "danger",
        });

        return false;
      }

      // Save to state
      context.commit("setWorkers", _arrayToMap(response.data.result.workers));

      return response.data.result;
    },
    // todo def
    async getWorkersForTeam(context, { contractId, teamId }) {
      let response = await api.getWorkersForTeam(contractId, teamId);
      return _arrayToMap(response.data.result.workers);
    },

    // todo def
    async getTransferWorkersForTeam(context, { teamId }) {
      let response = await api.getTransferWorkersForTeam(teamId);
      return _arrayToMap(response.data.result.workers);
    },

    async getWorker(context, { workerId }) {
      const teamId = this.state.activeTeamId;

      let fetched = await api.getWorker(teamId, workerId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get worker: ${workerId}`,
          variant: "danger",
        });

        return false;
      }

      // Save to state
      context.commit("setWorker", fetched.data.result.worker);

      // Also return worker to caller
      return fetched.data.result.worker;
    },
    async deleteGroup(context, { groupId }) {
      let response = await api.deleteGroup(groupId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not delete group`,
          variant: "danger",
        });

        return false;
      }

      context.commit("deleteGroup", groupId);

      return true;
    },
    async deleteWorker(context, { workerId }) {
      let response = await api.deleteWorker(workerId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not delete worker`,
          variant: "danger",
        });

        return false;
      }

      context.commit("deleteWorker", workerId);

      return true;
    },
    // todo def
    async postWorker(
      context,
      {
        name,
        deviceType,
        password,
        poolWorkerName,
        poolWorkerPassword,
        poolId,
        handle,
        serial_number,
        notes,
      }
    ) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let postWorkerFetch = await api.postWorker(
        teamId,
        name,
        deviceType,
        password,
        poolWorkerName,
        poolWorkerPassword,
        poolId,
        handle,
        serial_number,
        notes
      );

      if (!postWorkerFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create worker",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created worker: ${name}`,
        variant: "success",
      });

      context.commit("setWorker", postWorkerFetch.data.result.worker);

      return true;
    },
    async postWorkers(context, { workers }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let postWorkersFetch = await api.postWorkers(teamId, workers);

      if (!postWorkersFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create workers",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created ${workers.length} worker${
          workers.length == 1 ? "" : "s"
        }`,
        variant: "success",
      });

      postWorkersFetch.data.result.workers.forEach((worker) => {
        context.commit("setWorker", worker);
      });

      return postWorkersFetch.data.result.workers;
    },
    async postGroupWorkers(context, { groupId, workers }) {
      let response = await api.postGroupWorkers(groupId, workers);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create workers",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created ${workers.length} worker${
          workers.length == 1 ? "" : "s"
        }`,
        variant: "success",
      });

      response.data.result.workers.forEach((worker) => {
        context.commit("setWorker", worker);
      });

      return response.data.result.workers;
    },
    async getWorkerShares(context, { workerId, resolution }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getWorkerSharesFetch = await api.getWorkerShares(
        teamId,
        workerId,
        resolution
      );

      if (!getWorkerSharesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get worker shares",
          variant: "danger",
        });

        return false;
      }

      // Normalize worker share data
      const shareData = getWorkerSharesFetch.data.result.share_data;

      let normalized = [];

      let timezone = -new Date().getTimezoneOffset();
      let interval_start = new Date(
        new Date(shareData.interval_start).valueOf() + timezone * 60000
      );
      let interval_increment = 60000 * shareData.interval_increments;
      for (let i = 0; i < shareData.values.length; i++) {
        normalized.push([
          interval_start.getTime() + interval_increment * i,
          shareData.values[i],
        ]);
      }

      // Commit to state
      context.commit("setWorkerShares", {
        worker: workerId,
        newWorkerShares: normalized,
      });

      return true;
    },
    async getWorkerHashrate(context, { workerId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getWorkerHashrateFetch = await api.getWorkerHashrate(
        teamId,
        workerId
      );

      if (!getWorkerHashrateFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get worker hashrate",
          variant: "danger",
        });

        return false;
      }

      context.commit("setWorkerHashrate", {
        worker: workerId,
        hashrate: getWorkerHashrateFetch.data.result.hashrate,
      });
    },
    async getWorkerLastSubmission(context, { workerId }) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let getWorkerLastSubmissionFetch = await api.getWorkerLastSubmission(
        teamId,
        workerId
      );

      if (!getWorkerLastSubmissionFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get worker last submission",
          variant: "danger",
        });

        return false;
      }

      context.commit("setWorkerLastSubmission", {
        worker: workerId,
        lastSubmission:
          getWorkerLastSubmissionFetch.data.result.last_submission,
      });
    },
    async putWorker(
      context,
      {
        workerId,
        name,
        deviceType,
        password,
        poolWorkerName,
        poolWorkerPassword,
        poolId,
        handle,
        serial_number,
        notes,
      }
    ) {
      // todo: don't hardcode; turn into parameters
      const teamId = this.state.activeTeamId;

      let fetched = await api.putWorker(
        teamId,
        workerId,
        name,
        deviceType,
        password,
        poolWorkerName,
        poolWorkerPassword,
        poolId,
        handle,
        serial_number,
        notes
      );

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update worker: " + workerId,
          variant: "danger",
        });

        return false;
      }

      // Commit to state
      context.commit("setWorker", fetched.data.result.worker);

      return true;
    },

    async getDeviceTypes(context) {
      let getDevicesFetch = await api.getDeviceTypes();

      if (!getDevicesFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get device types",
          variant: "danger",
        });

        return false;
      }

      // Commit to state
      context.commit("setDeviceTypes", getDevicesFetch.data.result.devices);

      return true;
    },

    async getPools(context) {
      let getPoolsFetch = await api.getPools();

      if (!getPoolsFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get pools",
          variant: "danger",
        });

        return false;
      }

      // Commit to state
      context.commit("setPools", getPoolsFetch.data.result.pools);

      return true;
    },

    async getInactiveWorkers(
      context,
      { groupId, limit, submitted, offset, inactive }
    ) {
      // if groupId is set, teamId should be null, and vice versa.
      const teamId = groupId === null ? this.state.activeTeamId : null;

      let fetched = await api.getInactiveWorkers(
        groupId,
        teamId,
        limit,
        submitted,
        offset,
        inactive
      );

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get inactive workers for team",
          variant: "danger",
        });

        return false;
      }

      return fetched.data.result;
    },

    async postUptimeReport(
      context,
      { worker_ids, start_date, end_date, interval_seconds }
    ) {
      let postUptimeReportFetch = await api.postUptimeReport(
        worker_ids,
        start_date,
        end_date,
        interval_seconds
      );

      if (!postUptimeReportFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not initiate uptime report generation",
          variant: "danger",
        });

        return false;
      }

      return postUptimeReportFetch.data.result;
    },

    async getUptimeReport(context, { report_id }) {
      let getUptimeReportFetch = await api.getUptimeReport(report_id);

      if (!getUptimeReportFetch.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not download uptime report",
          variant: "danger",
        });

        return false;
      }

      return getUptimeReportFetch.data.result;
    },
    async getInvoices(context) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.getInvoices(teamId);
      if (fetched.ok) {
        context.commit("setInvoices", fetched.data.result.invoices);
      }
      return fetched;
    },

    async getDefaultPaymentMethod(context) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.getDefaultPaymentMethod(teamId);
      if (fetched.ok) {
        context.commit(
          "setDefaultPaymentMethod",
          fetched.data.result.payment_method
        );
      }
      return fetched.data;
    },

    async getTeamContracts(context) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.getTeamContracts(teamId);
      if (fetched.ok) {
        context.commit("setTeamContracts", fetched.data.result.contracts);
      }
      return fetched.data.result.contracts;
    },

    async getAllPaymentMethods(context) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.getAllPaymentMethods(teamId);
      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get your payment method",
          variant: "danger",
        });
      } else {
        let payment_methods = [];
        fetched.data.result.data.cards.forEach((pm) => {
          payment_methods.push(pm);
        });
        fetched.data.result.data.bank_accounts.forEach((ba) => {
          payment_methods.push(ba);
        });
        context.commit("setPaymentMethodArray", payment_methods);
      }
    },

    async changeDefaultPaymentMethod(context, { pm }) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.changeDefaultPaymentMethod(teamId, pm);
      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not change your default payment method",
          variant: "danger",
        });
      }
      return fetched;
    },

    async setTeamPoolSettings(context, { poolId, poolAccount, device_type }) {
      let teamId = this.state.activeTeamId;
      let fetched = await api.setTeamPoolSettings(
        teamId,
        poolId,
        poolAccount,
        device_type
      );

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not change your default payment method",
          variant: "danger",
        });
        return false;
      }
      return fetched.data.result;
    },

    async onboardClient(
      context,
      {
        template_id,
        signer_name,
        signer_email,
        team_id,
        team_name,
        batch_number,
        model,
        kwh_rate,
        term,
        quantity,
        setup_fee_per_miner,
        security_deposit_per_miner,
        insurance_per_miner,
        wattage,
        serials,
        shipping_provider,
        tracking_number,
        workers,
        start_date,
        expiration,
        ccEmailOne,
        ccEmailTwo,
        metadata,
      }
    ) {
      let response = await api.onboardClient(
        template_id,
        signer_name,
        signer_email,
        team_id,
        team_name,
        batch_number,
        model,
        kwh_rate,
        term,
        quantity,
        setup_fee_per_miner,
        security_deposit_per_miner,
        insurance_per_miner,
        wattage,
        serials,
        shipping_provider,
        tracking_number,
        workers,
        start_date,
        expiration,
        ccEmailOne,
        ccEmailTwo,
        metadata
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not onboard new client",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully onboarded new client`,
        variant: "success",
      });

      return response;
    },

    // todo def
    async getContracts(context, { limit, offset, searchString }) {
      let response = await api.getContracts(limit, offset, searchString);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not get contracts",
          variant: "danger",
        });

        return false;
      }

      return response.data.result;
    },

    async getContract(context, { contractId }) {
      let fetched = await api.getContract(contractId);

      if (!fetched.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not get contract with ID: ${contractId}`,
          variant: "danger",
        });

        return false;
      }

      // Also return worker to caller
      return fetched.data.result.contract;
    },

    async deleteContract(context, { contractId }) {
      let response = await api.deleteContract(contractId);

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: `Could not delete contract`,
          variant: "danger",
        });

        return false;
      }

      return true;
    },

    async postContract(
      context,
      {
        teamId,
        facilityId,
        batch_number,
        rate_per_kWh,
        machine_setup_fee,
        machine_security_deposit,
        machine_insurance,
        term_length,
        machine_count,
        customer_type,
        device_type_id,
        compatible,
        hourly_watt_consumption,
        shipped_or_transferred,
        selectedWorkerIds,
        invoiced_in_stripe,
        expiration,
        start_date,
      }
    ) {
      let response = await api.postContract(
        teamId,
        facilityId,
        batch_number,
        rate_per_kWh,
        machine_setup_fee,
        machine_security_deposit,
        machine_insurance,
        term_length,
        machine_count,
        customer_type,
        device_type_id,
        compatible,
        hourly_watt_consumption,
        shipped_or_transferred,
        selectedWorkerIds,
        invoiced_in_stripe,
        expiration,
        start_date
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not create new contract",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully created new contract`,
        variant: "success",
      });

      return true;
    },

    async putContract(
      context,
      {
        contractId,
        teamId,
        facilityId,
        batch_number,
        rate_per_kWh,
        machine_setup_fee,
        machine_security_deposit,
        machine_insurance,
        term_length,
        machine_count,
        customer_type,
        device_type_id,
        compatible,
        hourly_watt_consumption,
        onboarding_invoice_created,
        payment_method_ingested,
        pool_info_captured,
        shipped_or_transferred,
        selectedWorkerIds,
        invoiced_in_stripe,
        expiration,
        start_date,
      }
    ) {
      let response = await api.putContract(
        contractId,
        teamId,
        facilityId,
        batch_number,
        rate_per_kWh,
        machine_setup_fee,
        machine_security_deposit,
        machine_insurance,
        term_length,
        machine_count,
        customer_type,
        device_type_id,
        compatible,
        hourly_watt_consumption,
        onboarding_invoice_created,
        payment_method_ingested,
        pool_info_captured,
        shipped_or_transferred,
        selectedWorkerIds,
        invoiced_in_stripe,
        expiration,
        start_date
      );

      if (!response.ok) {
        context.commit("pushToast", {
          title: "Error",
          message: "Could not update contract.",
          variant: "danger",
        });

        return false;
      }

      context.commit("pushToast", {
        title: "Success",
        message: `Successfully updated contract`,
        variant: "success",
      });

      return true;
    },

    async putContractAsOnboardingUser(
      context,
      {
        teamId,
        contractId,
        onboarding_invoice_created,
        payment_method_ingested,
        pool_info_captured,
      }
    ) {
      let response = await api.putContractAsOnboardingUser(
        teamId,
        contractId,
        onboarding_invoice_created,
        payment_method_ingested,
        pool_info_captured
      );

      if (response.ok) {
        return response;
      }
    },

    async getHelloSignTemplates() {
      let response = await api.getHelloSignTemplates();
      if (response.ok) {
        return response.data.result.templates;
      }
    },
  },
});

store.subscribe((mutation, state) => {
  updateLocalStorage(state);
});

function _arrayToMap(arrayOfObjects, key = "id") {
  // Normalizes arrays into maps as so:
  //     Input format:  [ {id:id1, etc1}, {id:id2, etc2} ]
  //     Output format: { id1: {id,etc1}, id2: {id,etc2} }

  return arrayOfObjects.reduce(
    (accumulator, obj) => Object.assign(accumulator, { [obj[key]]: obj }),
    {}
  );
}

export default store;
